import { AppAccent } from 'src/app/core/types/app-accent.type';
import { AfterViewInit, Component, ElementRef, HostListener, Input, NgZone, OnChanges, OnDestroy, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { ModalService } from 'src/app/shared/services/modal.service';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-rich-text-viewer',
  templateUrl: './rich-text-viewer.component.html',
  styleUrl: './rich-text-viewer.component.css'
})
export class RichTextViewerComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() content: string | null | undefined = '';
  @Input() accent: AppAccent = 'solicitacao';
  @Input() appearance: 'field' | 'despacho' = 'field';
  @Input() showLabel = false;

  private static nextId = 0;
  readonly contentId = `rich-text-content-${RichTextViewerComponent.nextId++}`;
  isExpandable = false;
  expanded = false;
  opensModal = false;
  measured = false;
  contentHeight = 0;
  collapsedHeight = 120;
  private expandedHeight = 320;
  private observer?: ResizeObserver;
  private frame?: number;
  private modalOpen = false;

  @ViewChild('contentElement') private contentElement!: ElementRef<HTMLElement>;
  @ViewChild('viewport') private viewport!: ElementRef<HTMLElement>;
  @ViewChild('toggleButton') private toggleButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('modalDespacho') modalDespacho!: TemplateRef<unknown>;

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      this.observer = new ResizeObserver(() => this.scheduleMeasurement());
      this.observer.observe(this.contentElement.nativeElement);
      this.scheduleMeasurement();
    });
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleMeasurement();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    if (this.frame !== undefined) cancelAnimationFrame(this.frame);
  }

  toggleContent(): void {
    this.measureContent();
    if (!this.isExpandable) return;
    if (this.opensModal) {
      this.abrirModal();
      return;
    }
    this.expanded = !this.expanded;
  }

  private scheduleMeasurement(): void {
    if (!this.contentElement || this.frame !== undefined) return;
    this.zone.runOutsideAngular(() => {
      this.frame = requestAnimationFrame(() => {
        this.frame = undefined;
        this.measureContent();
      });
    });
  }

  private measureContent(): void {
    const content = this.contentElement.nativeElement;
    if (!content.getBoundingClientRect().width) return;
    const styles = getComputedStyle(this.viewport.nativeElement);
    const collapsed = parseFloat(styles.getPropertyValue('--rich-text-collapsed-height'));
    const expanded = parseFloat(styles.getPropertyValue('--rich-text-expanded-height'));
    const height = Math.ceil(content.getBoundingClientRect().height);
    if (this.measured && height === this.contentHeight && collapsed === this.collapsedHeight && expanded === this.expandedHeight) return;
    this.zone.run(() => {
      this.contentHeight = height;
      this.collapsedHeight = collapsed;
      this.expandedHeight = expanded;
      this.isExpandable = height > collapsed;
      this.opensModal = height > expanded;
      if (!this.isExpandable || this.opensModal) this.expanded = false;
      this.measured = true;
    });
  }

  constructor(private modalService: ModalService, private zone: NgZone) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['content']) this.expanded = false;
    this.scheduleMeasurement();
  }

  abrirModal(): void {
    if (this.modalOpen) return;
    this.modalOpen = true;
    const modal = this.modalService.openModalExtraLarge(this.modalDespacho);
    modal.addPanelClass('app-document-reader');
    modal.afterClosed().pipe(take(1)).subscribe(() => {
      this.modalOpen = false;
      this.toggleButton?.nativeElement.focus({ preventScroll: true });
    });
  }
}
